/**
 * SectionLabel — uppercase caption used as a section heading inside drawers,
 * detail panels, dashboards, and form inspectors.
 *
 * Replaces 60+ hand-rolled `textTransform: 'uppercase'` rolls across apps.
 *
 * Two ways to drive it:
 *
 *   1. Children mode (default) — pass the label as `children`, plus an
 *      optional right-aligned `hint` (count/note) and/or `action`:
 *
 *        <SectionLabel hint="3">ANSCHLÜSSE</SectionLabel>
 *
 *   2. i18nKey mode — pass an `i18nKey` prefix and the component resolves
 *      `<i18nKey>.label` for the heading and `<i18nKey>.hint` for an inline
 *      info-icon tooltip. Mirrors <FieldLabel> for list-style sections
 *      (checklist, decision options, photo slots) where the section IS the
 *      field. Adopted from process-designer's inspector convention in the
 *      Wave 7 E10 toolkit dedupe so the designer drops its shadow copy:
 *
 *        <SectionLabel i18nKey="process.field.manual.checklist" />
 *
 * Pair with FormSection / SectionCard for boxed layouts.
 */
import type { ReactNode } from 'react';
import { Caption1, makeStyles, tokens } from '@fluentui/react-components';
import { useLocale } from '../i18n';
import { HintTooltip } from './HintTooltip';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },
  label: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  hint: {
    color: tokens.colorNeutralForeground3,
    textTransform: 'none',
    letterSpacing: 'normal',
  },
});

interface SectionLabelChildrenProps {
  /** The uppercase label text (will be rendered as written; CSS handles casing). */
  children: ReactNode;
  /** Optional hint / count rendered to the right of the label. */
  hint?: ReactNode;
  /** Optional action element rendered at the right (button, link). */
  action?: ReactNode;
  i18nKey?: never;
  labelOverride?: never;
}

interface SectionLabelI18nProps {
  /**
   * i18n key prefix — resolves `<i18nKey>.label` (heading) and
   * `<i18nKey>.hint` (inline info-icon tooltip, skipped when missing).
   */
  i18nKey: string;
  /** Override the resolved label — escape hatch for dynamic strings. */
  labelOverride?: string;
  children?: never;
  hint?: never;
  action?: never;
}

type SectionLabelProps = SectionLabelChildrenProps | SectionLabelI18nProps;

export function SectionLabel(props: SectionLabelProps) {
  const styles = useStyles();
  const { t } = useLocale();

  if (props.i18nKey !== undefined) {
    const { i18nKey, labelOverride } = props;
    const hintKey = `${i18nKey}.hint`;
    const label = labelOverride ?? t(`${i18nKey}.label`);
    const hint = t(hintKey);
    const hasHint = hint !== hintKey && hint.trim() !== '';
    return (
      <div className={styles.root}>
        <Caption1 className={styles.label}>
          {label}
          {hasHint ? <HintTooltip text={hint} /> : null}
        </Caption1>
      </div>
    );
  }

  const { children, hint, action } = props;
  return (
    <div className={styles.root}>
      <Caption1 className={styles.label}>{children}</Caption1>
      {hint ? <Caption1 className={styles.hint}>{hint}</Caption1> : null}
      {action}
    </div>
  );
}
